import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { serviceFactory } from '@/services';
import { CareSpecialistService } from '@/services/features/CareSpecialistService.ts';

interface CareSpecialistAssignmentsResponse {
  clinic_ids: number[];
}

export const useCareSpecialistAssignments = () => {
  const { user, hasuraToken } = useAuth();
  const [clinicIds, setClinicIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get service instance
  const careSpecialistService = serviceFactory.getService(CareSpecialistService);

  useEffect(() => {
    const fetchAssignments = async () => {
      if (!user?.id || !hasuraToken) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        const response = await careSpecialistService.fetchCareSpecialistAssignments(Number(user.id));

        if (!response.success) {
          throw new Error(response.errors?.[0] || 'Failed to fetch care specialist assignments');
        }

        const assignmentsData = response.data?.pep_care_specialist_active_assignments;
        if (assignmentsData && assignmentsData.length > 0) {
          // Process clinic IDs using the service method
          const processedClinicIds = careSpecialistService.processClinicIds(assignmentsData);
          setClinicIds(processedClinicIds);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching care specialist assignments:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssignments();
  }, [user?.id, hasuraToken]);

  return { clinicIds, isLoading, error };
};